import PageAnimator from '../../components/ui/PageAnimator';
import React, { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { getEvents } from './calendrierAPI';
import CalendrierForm from './CalendrierForm';
import MiniLoader from '../../components/MiniLoader';
import { Button } from '../../components/ui/button';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Plus } from 'lucide-react';

const locales = {
  'fr': fr,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Custom Toolbar pour react-big-calendar
const CustomToolbar = (toolbar) => {
  const goToBack = () => {
    toolbar.date.setMonth(toolbar.date.getMonth() - 1);
    toolbar.onNavigate('PREV');
  };

  const goToNext = () => {
    toolbar.date.setMonth(toolbar.date.getMonth() + 1);
    toolbar.onNavigate('NEXT');
  };

  const goToCurrent = () => {
    const now = new Date();
    toolbar.date.setMonth(now.getMonth());
    toolbar.date.setYear(now.getFullYear());
    toolbar.onNavigate('TODAY');
  };

  const label = () => {
    return (
      <span className="text-lg font-bold text-primary capitalize">
        {format(toolbar.date, 'MMMM yyyy', { locale: fr })}
      </span>
    );
  };

  return (
    <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <div className="flex items-center gap-2">
        <Button variant="outline" size="icon" onClick={goToBack}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <Button variant="secondary" size="sm" onClick={goToCurrent}>
          Aujourd'hui
        </Button>
        <Button variant="outline" size="icon" onClick={goToNext}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      <div className="text-center">
        {label()}
      </div>

      <div className="flex items-center gap-1 bg-surface p-1 rounded-lg border border-[var(--hp-color-border-muted)]">
        {['month', 'week', 'day'].map((view) => (
          <Button
            key={view}
            variant={toolbar.view === view ? "default" : "ghost"}
            size="sm"
            onClick={() => toolbar.onView(view)}
          >
            {view === 'month' ? 'Mois' : view === 'week' ? 'Semaine' : 'Jour'}
          </Button>
        ))}
      </div>
    </div>
  );
};

export default function Calendrier() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const data = await getEvents();
      const formattedEvents = data.map(e => ({
        id: e.id,
        title: e.title,
        start: new Date(e.startDate),
        end: new Date(e.endDate),
        allDay: e.allDay,
        resource: e,
      }));
      setEvents(formattedEvents);
    } catch (error) {
      console.error("Erreur de chargement du calendrier", error);
    } finally {
      setLoading(false);
    }
  };

  const eventStyleGetter = (event) => {
    let backgroundColor = 'var(--hp-info-600)';
    
    if (event.resource.eventType === 'DEADLINE') backgroundColor = 'var(--hp-danger-600)';
    if (event.resource.eventType === 'CALL_FOR_FUNDS') backgroundColor = 'var(--hp-success-600)';
    if (event.resource.eventType === 'MEETING') backgroundColor = 'var(--hp-bronze-500)';

    return {
      style: {
        backgroundColor,
        borderRadius: '6px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block',
        padding: '2px 5px',
        fontSize: '0.875rem',
        fontWeight: '500',
        boxShadow: 'var(--hp-shadow-sm)'
      }
    };
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event.resource);
    setIsFormOpen(true);
  };

  if (loading) return <MiniLoader />;

  return (
    <div className="p-4 sm:p-8 z-10 h-full w-full flex flex-col">
      <PageAnimator>
        {/* Header Premium */}
        <div
          className="
            relative z-20
            flex shrink-0 flex-col
            gap-6
            overflow-visible
            workspace-card
            bg-cover bg-center bg-no-repeat
            p-4 sm:p-6
            mb-6
            xl:flex-row xl:items-center xl:justify-between
          "
          style={{ backgroundImage: "url('/assets/backgrounds/lighthouse_bg.png')" }}
        >
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 z-0 rounded-[var(--hp-radius-lg)] bg-overlay"
          />

          <div className="relative z-10 flex min-w-0 shrink-0 flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-secondary text-inverse shadow-hp-sm">
              <CalendarIcon className="h-6 w-6" />
            </div>

            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-inverse">
                Calendrier des opérations
              </h1>
              <p className="mt-1 text-sm text-inverse opacity-80">
                Gérez vos appels de fonds, échéances et rendez-vous.
              </p>
            </div>
          </div>

          <div className="relative z-10 flex items-center">
            <Button
              variant="accent"
              className="shadow-hp-sm whitespace-nowrap border-transparent"
              onClick={() => {
                setSelectedEvent(null);
                setIsFormOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Nouvel événement
            </Button>
          </div>
        </div>

        {/* Calendrier Container */}
        <div className="workspace-card-elevated p-6 h-[750px]">
          <style dangerouslySetInnerHTML={{__html: `
            .rbc-calendar { font-family: inherit; }
            .rbc-header { padding: 10px 0; font-weight: 600; color: var(--hp-color-text-muted); border-bottom: 1px solid var(--hp-color-border-muted); }
            .rbc-month-view { border: 1px solid var(--hp-color-border-muted); border-radius: var(--hp-radius-lg); overflow: hidden; }
            .rbc-day-bg + .rbc-day-bg { border-left: 1px solid var(--hp-color-border-muted); }
            .rbc-month-row + .rbc-month-row { border-top: 1px solid var(--hp-color-border-muted); }
            .rbc-date-cell { padding: 4px 8px; font-weight: 500; }
            .rbc-off-range-bg { background-color: var(--hp-color-surface-muted); }
            .rbc-today { background-color: var(--hp-info-50); }
            .rbc-event { padding: 0; cursor: pointer; }
            .rbc-time-view { border: 1px solid var(--hp-color-border-muted); border-radius: var(--hp-radius-lg); overflow: hidden; }
            .rbc-timeslot-group { border-bottom: 1px solid var(--hp-color-border-muted); min-height: 40px; }
            .rbc-time-header-content { border-left: 1px solid var(--hp-color-border-muted); }
          `}} />
          
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={handleSelectEvent}
            components={{
              toolbar: CustomToolbar
            }}
            messages={{
              next: "Suivant",
              previous: "Précédent",
              today: "Aujourd'hui",
              month: "Mois",
              week: "Semaine",
              day: "Jour",
              showMore: total => `+ ${total} plus`
            }}
          />
        </div>

        <CalendrierForm 
          open={isFormOpen} 
          event={selectedEvent}
          onClose={() => {
            setIsFormOpen(false);
            setSelectedEvent(null);
          }} 
          onSaveSuccess={fetchEvents}
        />
      </PageAnimator>
    </div>
  );
}
